import { FILTER_GROUP_TYPES } from '@/shared/api/openmetadata';

import { isExcludedFromDiscoveryAssetTypeFilter } from '@/entities/asset-search/domain/constants';
import type { FilterState, QuickFilter } from '@/entities/discovery/domain/types';

const sanitiseDiscoveryAssetTypes = (types: string[]): string[] =>
  types.filter((t) => !isExcludedFromDiscoveryAssetTypeFilter(t));

const QUERY_PARAM = 'q' as const;

export type SearchURLParams = {
  q?: string;
  assetType?: string[];
  domain?: string[];
  owner?: string[];
  tags?: string[];
  tagsOperator?: 'AND' | 'OR';
  tier?: string[];
  certification?: string[];
  dataSource?: string[];
  quickFilter?: string;
};

export const encodeSearchParams = (query: string, filters: FilterState): SearchURLParams => {
  const params: SearchURLParams = {};

  if (query) {
    params.q = query;
  }

  const assetTypesFromFilters = sanitiseDiscoveryAssetTypes(filters[FILTER_GROUP_TYPES.ASSET_TYPE]);
  if (assetTypesFromFilters.length > 0) {
    params.assetType = assetTypesFromFilters;
  }

  if (filters[FILTER_GROUP_TYPES.DOMAIN].length > 0) {
    params.domain = filters[FILTER_GROUP_TYPES.DOMAIN];
  }

  if (filters[FILTER_GROUP_TYPES.OWNER].length > 0) {
    params.owner = filters[FILTER_GROUP_TYPES.OWNER];
  }

  if (filters[FILTER_GROUP_TYPES.TAGS].length > 0) {
    params.tags = filters[FILTER_GROUP_TYPES.TAGS];
    if (filters.tagsOperator) {
      params.tagsOperator = filters.tagsOperator;
    }
  }

  if (filters[FILTER_GROUP_TYPES.TIER].length > 0) {
    params.tier = filters[FILTER_GROUP_TYPES.TIER];
  }

  if (filters[FILTER_GROUP_TYPES.CERTIFICATION].length > 0) {
    params.certification = filters[FILTER_GROUP_TYPES.CERTIFICATION];
  }

  if (filters[FILTER_GROUP_TYPES.DATA_SOURCE].length > 0) {
    params.dataSource = filters[FILTER_GROUP_TYPES.DATA_SOURCE];
  }

  if (filters.quickFilter) {
    params.quickFilter = JSON.stringify(filters.quickFilter);
  }

  return params;
};

export const decodeSearchParams = (searchParams: URLSearchParams): { query: string; filters: FilterState } => {
  const query = searchParams.get(QUERY_PARAM) || '';
  const tagsOperatorParam = searchParams.get('tagsOperator');
  const quickFilterParam = searchParams.get('quickFilter');

  let quickFilter: QuickFilter | undefined;
  if (quickFilterParam) {
    try {
      quickFilter = JSON.parse(quickFilterParam) as QuickFilter;
    } catch (error) {
      console.error('Failed to parse quickFilter:', error);
    }
  }

  const filters: FilterState = {
    [FILTER_GROUP_TYPES.ASSET_TYPE]: sanitiseDiscoveryAssetTypes(searchParams.getAll(FILTER_GROUP_TYPES.ASSET_TYPE)),
    [FILTER_GROUP_TYPES.DOMAIN]: searchParams.getAll(FILTER_GROUP_TYPES.DOMAIN),
    [FILTER_GROUP_TYPES.OWNER]: searchParams.getAll(FILTER_GROUP_TYPES.OWNER),
    [FILTER_GROUP_TYPES.TAGS]: searchParams.getAll(FILTER_GROUP_TYPES.TAGS),
    tagsOperator: tagsOperatorParam === 'AND' || tagsOperatorParam === 'OR' ? tagsOperatorParam : 'OR',
    [FILTER_GROUP_TYPES.TIER]: searchParams.getAll(FILTER_GROUP_TYPES.TIER),
    [FILTER_GROUP_TYPES.CERTIFICATION]: searchParams.getAll(FILTER_GROUP_TYPES.CERTIFICATION),
    [FILTER_GROUP_TYPES.DATA_SOURCE]: searchParams.getAll(FILTER_GROUP_TYPES.DATA_SOURCE),
    quickFilter,
  };

  return { query, filters };
};

export const buildSearchURL = (query: string, filters: FilterState): string => {
  const params = new URLSearchParams();

  if (query) {
    params.set(QUERY_PARAM, query);
  }

  sanitiseDiscoveryAssetTypes(filters[FILTER_GROUP_TYPES.ASSET_TYPE]).forEach((type) =>
    params.append(FILTER_GROUP_TYPES.ASSET_TYPE, type)
  );
  filters[FILTER_GROUP_TYPES.DOMAIN].forEach((domain) => params.append(FILTER_GROUP_TYPES.DOMAIN, domain));
  filters[FILTER_GROUP_TYPES.OWNER].forEach((owner) => params.append(FILTER_GROUP_TYPES.OWNER, owner));
  filters[FILTER_GROUP_TYPES.TAGS].forEach((tag) => params.append(FILTER_GROUP_TYPES.TAGS, tag));

  if (filters.tagsOperator && filters[FILTER_GROUP_TYPES.TAGS].length > 0) {
    params.set('tagsOperator', filters.tagsOperator);
  }

  filters[FILTER_GROUP_TYPES.TIER].forEach((tier) => params.append(FILTER_GROUP_TYPES.TIER, tier));
  filters[FILTER_GROUP_TYPES.CERTIFICATION].forEach((cert) => params.append(FILTER_GROUP_TYPES.CERTIFICATION, cert));
  filters[FILTER_GROUP_TYPES.DATA_SOURCE].forEach((source) => params.append(FILTER_GROUP_TYPES.DATA_SOURCE, source));

  if (filters.quickFilter) {
    params.set('quickFilter', JSON.stringify(filters.quickFilter));
  }

  const queryString = params.toString();
  return queryString ? `/search?${queryString}` : '/search';
};

export const updateSearchURL = (query: string, filters: FilterState): void => {
  const url = buildSearchURL(query, filters);
  window.history.replaceState({}, '', url);
};
